import { useEffect, useState } from "react";
import { Button } from "components/ui/Button";
import { LegalDocumentContent } from "components/LegalDocumentContent";
import fetchWithCredentials from "apiClient";
import { AlertTriangle, FileSpreadsheet } from "lucide-react";
import { Modal } from "components/ui/Modal";
import { TermsModal } from "components/TermsModal";
import { useAuth } from "context/AuthContext";
import { MOBILE_PAGE_TITLE_MENU_INSET } from "constants/pageLayout";
import type { LegalAcceptancePayload, LegalDocumentPayload } from "types/Legal";
import * as XLSX from "xlsx";
import type { Condo } from "types/condo";
import type { Resident } from "types/Resident";
import { parseResidentsListResponse } from "utils/residentsListResponse";

type CondoUnit = {
  id: number;
  name: string;
};

export default function Account() {
  const [subscriptionQuantity, setSubscriptionQuantity] = useState<
    number | null
  >(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(
    null,
  );
  const [subscriptionExpiresAt, setSubscriptionExpiresAt] = useState<
    string | null
  >(null);
  const [subscriptionNextBillingAt, setSubscriptionNextBillingAt] = useState<
    string | null
  >(null);
  const [subscriptionPrice, setSubscriptionPrice] = useState<number | null>(
    null,
  );
  const [subscriptionNextBillingQuantity, setSubscriptionNextBillingQuantity] =
    useState<number | null>(null);
  const [subscriptionNextBillingAmount, setSubscriptionNextBillingAmount] =
    useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [termsAcceptances, setTermsAcceptances] = useState<
    LegalAcceptancePayload[]
  >([]);
  const [termsAcceptancesLoading, setTermsAcceptancesLoading] = useState(true);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [currentTermsForModal, setCurrentTermsForModal] =
    useState<LegalDocumentPayload | null>(null);
  const [showAcceptedDocumentModal, setShowAcceptedDocumentModal] =
    useState(false);
  const { refetchProfile, lang, isDemo, isAdmin } = useAuth();
  const locale = lang === "es" ? "es" : "en";
  const dateLocale = locale === "es" ? "es-ES" : "en-US";
  const tr = (es: string, en: string) => (locale === "es" ? es : en);
  const [updatePaymentMethodLoading, setUpdatePaymentMethodLoading] =
    useState(false);
  const [updatePaymentMethodError, setUpdatePaymentMethodError] = useState<
    string | null
  >(null);
  const [exportingUnitsAndResidents, setExportingUnitsAndResidents] =
    useState(false);
  const [exportUnitsAndResidentsError, setExportUnitsAndResidentsError] =
    useState<string | null>(null);

  useEffect(() => {
    async function fetchSettings() {
      try {
        const data = await fetchWithCredentials(
          `/manager-api/property-manager/settings/`,
        );
        setSubscriptionQuantity(
          data.subscriptionQuantity != null ? data.subscriptionQuantity : null,
        );
        setSubscriptionStatus(
          data.subscriptionStatus != null ? data.subscriptionStatus : null,
        );
        setSubscriptionExpiresAt(
          data.subscriptionExpiresAt != null
            ? data.subscriptionExpiresAt
            : null,
        );
        setSubscriptionNextBillingAt(
          data.subscriptionNextBillingAt != null
            ? data.subscriptionNextBillingAt
            : null,
        );
        setSubscriptionPrice(
          typeof data.subscriptionPrice === "number"
            ? data.subscriptionPrice
            : null,
        );
        setSubscriptionNextBillingQuantity(
          typeof data.subscriptionNextBillingQuantity === "number"
            ? data.subscriptionNextBillingQuantity
            : null,
        );
        setSubscriptionNextBillingAmount(
          typeof data.subscriptionNextBillingAmount === "number"
            ? data.subscriptionNextBillingAmount
            : null,
        );
      } catch (err) {
        console.error("Error loading account:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchSettings();
  }, []);

  useEffect(() => {
    async function fetchTermsAcceptances() {
      try {
        const list = await fetchWithCredentials("/api/legal/me/?slug=terms");
        setTermsAcceptances(Array.isArray(list) ? list : []);
      } catch {
        setTermsAcceptances([]);
      } finally {
        setTermsAcceptancesLoading(false);
      }
    }
    fetchTermsAcceptances();
  }, []);

  const handleReviewAndAccept = async () => {
    try {
      const doc = await fetchWithCredentials("/api/legal/current/?slug=terms");
      setCurrentTermsForModal(doc);
      setShowTermsModal(true);
    } catch (err) {
      console.error("Error loading terms:", err);
    }
  };

  const handleTermsAccepted = () => {
    setShowTermsModal(false);
    setCurrentTermsForModal(null);
    refetchProfile();
    fetchWithCredentials("/api/legal/me/?slug=terms").then((list) =>
      setTermsAcceptances(Array.isArray(list) ? list : []),
    );
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await fetchWithCredentials(
        `/manager-api/property-manager/cancel-subscription/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      setShowCancelDialog(false);
      const data = await fetchWithCredentials(
        `/manager-api/property-manager/settings/`,
      );
      setSubscriptionQuantity(
        data.subscriptionQuantity != null ? data.subscriptionQuantity : null,
      );
      setSubscriptionStatus(
        data.subscriptionStatus != null ? data.subscriptionStatus : null,
      );
      setSubscriptionExpiresAt(
        data.subscriptionExpiresAt != null ? data.subscriptionExpiresAt : null,
      );
      setSubscriptionNextBillingAt(
        data.subscriptionNextBillingAt != null
          ? data.subscriptionNextBillingAt
          : null,
      );
      setSubscriptionPrice(
        typeof data.subscriptionPrice === "number"
          ? data.subscriptionPrice
          : null,
      );
      setSubscriptionNextBillingQuantity(
        typeof data.subscriptionNextBillingQuantity === "number"
          ? data.subscriptionNextBillingQuantity
          : null,
      );
      setSubscriptionNextBillingAmount(
        typeof data.subscriptionNextBillingAmount === "number"
          ? data.subscriptionNextBillingAmount
          : null,
      );
    } catch (err) {
      console.error("Error cancelling subscription:", err);
    } finally {
      setCancelling(false);
    }
  };

  const handleResetDemo = async () => {
    setResetting(true);
    setResetError(null);
    try {
      await fetchWithCredentials(`/manager-api/property-manager/reset-demo/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ confirm: true }),
      });
      window.location.href = "/";
    } catch (err) {
      console.error("Error resetting demo account:", err);
      setResetError(
        err instanceof Error
          ? err.message
          : tr("No se pudo reiniciar la cuenta.", "Could not reset the account."),
      );
      setResetting(false);
    }
  };

  const subscriptionStatusLabelES: Record<string, string> = {
    trial: "Prueba",
    trial_expired: "Prueba vencida",
    active: "Activo",
    dunning: "Pago pendiente",
    updating_payment_method: "Actualizando método de pago",
    inactive: "Inactivo",
    free: "Gratis",
  };

  const subscriptionStatusLabelEN: Record<string, string> = {
    trial: "Trial",
    trial_expired: "Trial expired",
    active: "Active",
    dunning: "Payment pending",
    updating_payment_method: "Updating payment method",
    inactive: "Inactive",
    free: "Free",
  };

  const getSubscriptionStatusLabel = (status: string | null) =>
    status
      ? locale === "es"
        ? (subscriptionStatusLabelES[status] ?? status)
        : (subscriptionStatusLabelEN[status] ?? status)
      : "—";

  const formatExpiresAt = (iso: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    return d.toLocaleDateString(dateLocale, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatExpiresAtDateOnly = (iso: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    return d.toLocaleDateString(dateLocale, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const expiresAtDateOnly = formatExpiresAtDateOnly(subscriptionExpiresAt);
  const formattedNextBillingAt = formatExpiresAt(subscriptionNextBillingAt);
  const unitPrice =
    subscriptionPrice != null && !Number.isNaN(subscriptionPrice)
      ? subscriptionPrice
      : 0.7;

  const handleUpdatePaymentMethod = async () => {
    setUpdatePaymentMethodLoading(true);
    setUpdatePaymentMethodError(null);
    try {
      const data = await fetchWithCredentials(
        "/manager-api/property-manager/update-payment-method/",
        { method: "POST" },
      );
      const redirectUrl = data?.redirectUrl;
      if (redirectUrl && typeof redirectUrl === "string") {
        window.location.href = redirectUrl;
        return;
      }
    } catch (err) {
      setUpdatePaymentMethodError(
        err instanceof Error
          ? err.message
          : tr(
              "Error al actualizar el método de pago",
              "Error updating payment method",
            ),
      );
    } finally {
      setUpdatePaymentMethodLoading(false);
    }
  };

  const formatTypeLabel = (
    type: Resident["type"],
    currentLocale: "es" | "en",
  ) => {
    if (currentLocale === "es") {
      if (type === "owner") return "Propietario";
      if (type === "tenant") return "Inquilino";
      return "Propietario no residente";
    }
    if (type === "owner") return "Owner";
    if (type === "tenant") return "Tenant";
    return "Non-resident owner";
  };

  const handleExportUnitsAndResidents = async () => {
    setExportingUnitsAndResidents(true);
    setExportUnitsAndResidentsError(null);
    try {
      const condos: Condo[] = await fetchWithCredentials(
        "/manager-api/condos/",
      );
      const allCondoData = await Promise.all(
        condos.map(async (condo) => {
          const [residentsPayload, units] = await Promise.all([
            fetchWithCredentials(`/manager-api/residents/?condo=${condo.id}`),
            fetchWithCredentials(`/manager-api/units/?condo=${condo.id}`),
          ]);
          return {
            condo,
            residents: parseResidentsListResponse(residentsPayload).residents,
            units: (Array.isArray(units) ? units : []) as CondoUnit[],
          };
        }),
      );

      const rows = allCondoData.flatMap(({ condo, residents, units }) => {
        if (!units.length) {
          return [
            {
              condoName: condo.name,
              condoMode: condo.mode,
              condoIsLive: condo.mode === "live",
              unitName: "",
              residentName: "",
              residentType: "",
              residentIsActive: null as boolean | null,
            },
          ];
        }

        const residentsByUnit = residents.reduce(
          (acc, resident) => {
            const unitKey = resident.unitName ?? "";
            if (!acc[unitKey]) {
              acc[unitKey] = [];
            }
            acc[unitKey].push(resident);
            return acc;
          },
          {} as Record<string, Resident[]>,
        );

        return units.flatMap((unit) => {
          const unitResidents = residentsByUnit[unit.name] ?? [];
          if (!unitResidents.length) {
            return [
              {
                condoName: condo.name,
                condoMode: condo.mode,
                condoIsLive: condo.mode === "live",
                unitName: unit.name,
                residentName: "",
                residentType: "",
                residentIsActive: null as boolean | null,
              },
            ];
          }

          return unitResidents.map((resident) => ({
            condoName: condo.name,
            condoMode: condo.mode,
            condoIsLive: condo.mode === "live",
            unitName: unit.name,
            residentName: resident.contact?.name ?? "",
            residentType: resident.type,
            residentIsActive: resident.isActive ?? true,
          }));
        });
      });

      const activeLiveUnitsCount = allCondoData.reduce(
        (count, { condo, units }) =>
          count + (condo.mode === "live" ? units.length : 0),
        0,
      );

      const totalResidents = rows.filter((row) => row.residentName).length;
      const totalActiveResidents = rows.filter(
        (row) => row.residentName && row.residentIsActive === true,
      ).length;

      const summaryRows = [
        {
          [tr("Métrica", "Metric")]: tr(
            "Total unidades activas (condos live)",
            "Total active units (live condos)",
          ),
          [tr("Valor", "Value")]: activeLiveUnitsCount,
        },
        {
          [tr("Métrica", "Metric")]: tr("Total residentes", "Total residents"),
          [tr("Valor", "Value")]: totalResidents,
        },
        {
          [tr("Métrica", "Metric")]: tr(
            "Total residentes activos",
            "Total active residents",
          ),
          [tr("Valor", "Value")]: totalActiveResidents,
        },
      ];

      const detailsRows = rows.map((row) => ({
        [tr("Condominio", "Condo")]: row.condoName,
        [tr("Modo condo", "Condo mode")]: row.condoMode,
        [tr("Condo live", "Condo live")]: row.condoIsLive
          ? tr("Sí", "Yes")
          : tr("No", "No"),
        [tr("Unidad", "Unit")]: row.unitName || "—",
        [tr("Residente", "Resident")]: row.residentName || "—",
        [tr("Tipo residente", "Resident type")]: row.residentType
          ? formatTypeLabel(row.residentType as Resident["type"], locale)
          : "—",
        [tr("Residente activo", "Resident active")]: row.residentName
          ? row.residentIsActive === true
            ? tr("Sí", "Yes")
            : tr("No", "No")
          : "—",
      }));

      const workbook = XLSX.utils.book_new();
      const summarySheet = XLSX.utils.json_to_sheet(summaryRows);
      const detailsSheet = XLSX.utils.json_to_sheet(detailsRows);
      XLSX.utils.book_append_sheet(
        workbook,
        summarySheet,
        tr("Resumen", "Summary"),
      );
      XLSX.utils.book_append_sheet(
        workbook,
        detailsSheet,
        tr("Unidades y residentes", "Units & Residents"),
      );

      const now = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `units-residents-${now}.xlsx`);
    } catch (err) {
      console.error("Error exporting units and residents:", err);
      setExportUnitsAndResidentsError(
        err instanceof Error
          ? err.message
          : tr(
              "No se pudo generar el Excel.",
              "Could not generate the Excel file.",
            ),
      );
    } finally {
      setExportingUnitsAndResidents(false);
    }
  };

  return (
    <div className="w-full h-full max-w-7xl overflow-y-auto bg-white py-4 max-lg:pl-0 max-lg:pr-4 lg:px-6">
      <h1
        className={`mb-6 text-2xl font-bold text-gray-900 ${MOBILE_PAGE_TITLE_MENU_INSET}`}
      >
        {tr("Cuenta", "Account")}
      </h1>
      {loading ? (
        <p className="text-xs text-gray-500 max-lg:pl-4 lg:pl-0 lg:text-sm">
          {tr("Cargando...", "Loading...")}
        </p>
      ) : (
        <div className="max-lg:pl-4 lg:pl-0">
          <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4 lg:p-5 max-w-md">
            <div className="space-y-5">
              <div className="pb-4 border-b border-gray-200">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-gray-600">
                    {tr("Unidades activas", "Active units")}
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={handleExportUnitsAndResidents}
                    disabled={exportingUnitsAndResidents}
                    className="inline-flex items-center gap-1.5"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                    {exportingUnitsAndResidents
                      ? tr("Generando...", "Generating...")
                      : tr("Descargar detalle", "Download detail")}
                  </Button>
                </div>
                <p className="text-3xl font-bold text-gray-900 tracking-tight">
                  {subscriptionQuantity != null ? subscriptionQuantity : "—"}
                </p>
                {exportUnitsAndResidentsError && (
                  <p className="mt-2 text-sm text-red-600">
                    {exportUnitsAndResidentsError}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">
                  {tr("Estado", "Status")}
                </p>
                {subscriptionStatus === "scheduled_for_cancellation" &&
                expiresAtDateOnly ? (
                  <span className="text-sm font-medium text-red-600">
                    {tr("Expira el", "Expires on")} {expiresAtDateOnly}
                  </span>
                ) : (
                  <span
                    className={`
                      inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium
                      ${
                        subscriptionStatus === "active" ||
                        subscriptionStatus === "trial" ||
                        subscriptionStatus === "free"
                          ? "bg-green-100 text-green-800"
                          : subscriptionStatus === "trial_expired" ||
                              subscriptionStatus === "dunning" ||
                              subscriptionStatus === "updating_payment_method"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-gray-100 text-gray-700"
                      }
                    `}
                  >
                    {getSubscriptionStatusLabel(subscriptionStatus)}
                  </span>
                )}
                {subscriptionStatus !== "trial" && (
                  <p className="mt-2">
                    <button
                      type="button"
                      onClick={handleUpdatePaymentMethod}
                      disabled={updatePaymentMethodLoading}
                      className={`text-sm font-medium underline underline-offset-2 bg-transparent border-0 p-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                        subscriptionStatus === "dunning" ||
                        subscriptionStatus === "updating_payment_method"
                          ? "text-amber-800 hover:text-amber-900"
                          : "text-gray-700 hover:text-gray-900"
                      }`}
                    >
                      {updatePaymentMethodLoading
                        ? tr("Cargando...", "Loading...")
                        : tr(
                            "Actualizar método de pago",
                            "Update payment method",
                          )}
                    </button>
                    {updatePaymentMethodError && (
                      <span className="block mt-1 text-sm text-red-600">
                        {updatePaymentMethodError}
                      </span>
                    )}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">
                  {tr("Plan", "Plan")}
                </p>
                <p className="text-sm text-gray-900">
                  Eloria – {tr("Unidad activa", "Active unit")}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">
                  {tr("Precio", "Price")}
                </p>
                <p className="text-sm text-gray-900">
                  {`$${unitPrice.toFixed(2)} ${locale === "es" ? "por unidad" : "per unit"}`}
                  {subscriptionQuantity != null && subscriptionQuantity > 0 && (
                    <span className="text-gray-600">
                      {" "}
                      · {tr("Total", "Total")}{" "}
                      <span className="font-semibold text-gray-900">
                        ${(subscriptionQuantity * unitPrice).toFixed(2)}
                      </span>{" "}
                      / {tr("mes", "month")}
                    </span>
                  )}
                </p>
              </div>
              {formattedNextBillingAt && subscriptionStatus !== "inactive" && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-0.5">
                    {tr("Próxima facturación", "Next billing")}
                  </p>
                  <p className="text-sm text-gray-900">
                    {formattedNextBillingAt}
                    {subscriptionNextBillingAmount != null && (
                      <>
                        {" · "}
                        <span className="font-semibold text-gray-900">
                          ${subscriptionNextBillingAmount.toFixed(2)}
                        </span>
                        {subscriptionNextBillingQuantity != null &&
                          subscriptionNextBillingQuantity > 0 && (
                            <span className="text-gray-600">
                              {" "}
                              (
                              {subscriptionNextBillingQuantity}{" "}
                              {subscriptionNextBillingQuantity === 1
                                ? tr("unidad", "unit")
                                : tr("unidades", "units")}
                              )
                            </span>
                          )}
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>

          {subscriptionStatus &&
            subscriptionStatus !== "inactive" &&
            subscriptionStatus !== "trial_expired" &&
            subscriptionStatus !== "trial" && (
              <div className="pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="border-red-200 text-red-700 hover:bg-red-50 hover:border-red-300"
                  onClick={() => setShowCancelDialog(true)}
                >
                  {tr("Cancelar suscripción", "Cancel subscription")}
                </Button>
              </div>
            )}

          <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4 lg:p-5 max-w-md">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">
              {tr("Términos y Condiciones", "Terms and Conditions")}
            </h2>
            {termsAcceptancesLoading ? (
              <p className="text-sm text-gray-500">
                {tr("Cargando...", "Loading...")}
              </p>
            ) : termsAcceptances.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm text-gray-700">
                  {tr("Accepted:", "Accepted:")} {tr("versión", "version")}{" "}
                  <span className="font-medium">
                    {termsAcceptances[0].legalDocument.version}
                  </span>{" "}
                  {tr("el", "on")}{" "}
                  {new Date(termsAcceptances[0].acceptedAt).toLocaleDateString(
                    dateLocale,
                    {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    },
                  )}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowAcceptedDocumentModal(true)}
                  >
                    {tr("Ver documento", "View document")}
                  </Button>
                </div>
                <details className="text-xs text-gray-500">
                  <summary className="cursor-pointer hover:text-gray-700">
                    {tr("Ver hash del documento", "View document hash")}
                  </summary>
                  <code className="block mt-1 break-all font-mono">
                    {termsAcceptances[0].legalDocument.contentHash}
                  </code>
                </details>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-amber-700">
                  {tr("Pendiente de aceptación", "Acceptance pending")}
                </p>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={handleReviewAndAccept}
                >
                  {tr("Revisar y aceptar", "Review and accept")}
                </Button>
              </div>
            )}
          </div>
        </div>
        </div>
      )}

      {isDemo && isAdmin && (
        <div className="mt-10 max-w-md max-lg:pl-4 lg:pl-0">
          <button
            type="button"
            onClick={() => {
              setResetError(null);
              setShowResetDialog(true);
            }}
            className="bg-transparent border-0 p-0 text-[11px] text-gray-400 hover:text-gray-500 cursor-pointer"
          >
            {tr("Reiniciar datos", "Reset data")}
          </button>
        </div>
      )}

      {showTermsModal && currentTermsForModal && (
        <TermsModal
          document={currentTermsForModal}
          onAccepted={handleTermsAccepted}
        />
      )}

      <Modal
        isOpen={showAcceptedDocumentModal}
        onClose={() => setShowAcceptedDocumentModal(false)}
        title={
          tr(
            `Términos y Condiciones (versión ${termsAcceptances[0]?.legalDocument?.version ?? ""})`,
            `Terms and Conditions (version ${termsAcceptances[0]?.legalDocument?.version ?? ""})`,
          ) as string
        }
      >
        <div className="max-h-[70vh] overflow-y-auto">
          <LegalDocumentContent
            content={termsAcceptances[0]?.legalDocument?.content ?? ""}
          />
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setShowAcceptedDocumentModal(false)}
          >
            {tr("Cerrar", "Close")}
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        title={tr("Cancelar Suscripción", "Cancel Subscription")}
      >
        <div className="mb-6">
          <div className="flex items-start space-x-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-700">
                {tr(
                  "¿Estás seguro de que quieres cancelar tu suscripción?",
                  "Are you sure you want to cancel your subscription?",
                )}
              </p>
              <p className="text-sm text-gray-600 mt-2">
                {tr(
                  "Tu suscripción permanecerá activa hasta el próximo ciclo de facturación. Puedes volver a suscribirte en cualquier momento.",
                  "Your subscription will remain active until the next billing cycle. You can subscribe again at any time.",
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="flex space-x-3">
          <Button
            onClick={() => setShowCancelDialog(false)}
            variant="secondary"
            size="sm"
            className="flex-1"
            disabled={cancelling}
          >
            {tr("Mantener Suscripción", "Keep Subscription")}
          </Button>
          <Button
            onClick={handleCancelSubscription}
            variant="secondary"
            size="sm"
            className="flex-1 bg-red-600 hover:bg-red-700 text-white border-red-600"
            disabled={cancelling}
          >
            {cancelling
              ? tr("Cancelando...", "Cancelling...")
              : tr("Cancelar Suscripción", "Cancel Subscription")}
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={showResetDialog}
        onClose={() => {
          if (!resetting) {
            setShowResetDialog(false);
          }
        }}
        title={tr("Reiniciar datos", "Reset data")}
      >
        <div className="mb-6">
          <p className="text-sm text-gray-700">
            {tr(
              "Se borran conversaciones, casos, knowledge y documentos de esta cuenta demo. Edificios, personas y la conexión de WhatsApp se mantienen.",
              "This deletes conversations, cases, knowledge, and documents on this demo account. Buildings, people, and the WhatsApp connection stay.",
            )}
          </p>
          {resetError && (
            <p className="mt-3 text-sm text-red-600">{resetError}</p>
          )}
        </div>
        <div className="flex space-x-3">
          <Button
            onClick={() => setShowResetDialog(false)}
            variant="secondary"
            size="sm"
            className="flex-1"
            disabled={resetting}
          >
            {tr("Cancelar", "Cancel")}
          </Button>
          <Button
            onClick={handleResetDemo}
            variant="secondary"
            size="sm"
            className="flex-1"
            disabled={resetting}
          >
            {resetting
              ? tr("Reiniciando...", "Resetting...")
              : tr("Reiniciar", "Reset")}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
